/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'

import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Unstable_Grid2'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Skeleton from '@mui/material/Skeleton'
import Tooltip from '@mui/material/Tooltip'
import Checkbox from '@mui/material/Checkbox'
import Collapse from '@mui/material/Collapse'
import Button from '@mui/material/Button'

import useDebounce from 'src/hooks/use-debounce'
import { useBoolean } from 'src/hooks/use-boolean'
import Iconify from 'src/components/iconify'
import Scrollbar from 'src/components/scrollbar'
import { useSettingsContext } from 'src/components/settings'
import {
  AuditOrderBy,
  AuditOverview,
  AuditOverviewRow,
  fetchAuditAlerts,
  fetchAuditOverview,
  markAuditAlertReviewed,
} from 'src/service/audit.service'

import { formatCurrency } from '../orders/format-currency'
import AuditEventDialog from './audit-event-dialog'

const fmtDateTime = (value?: string | null) => {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

export default function AuditView() {
  const settings = useSettingsContext()
  const isLoading = useBoolean(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [orderBy, setOrderBy] = useState<AuditOrderBy>('date')
  const [overview, setOverview] = useState<AuditOverview | null>(null)
  const [alerts, setAlerts] = useState<AuditOverviewRow[]>([])
  const [reviewedAlerts, setReviewedAlerts] = useState<AuditOverviewRow[]>([])
  const [showReviewed, setShowReviewed] = useState(false)
  const [markingReviewId, setMarkingReviewId] = useState<string | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<AuditOverviewRow | null>(null)
  const searchDebounce = useDebounce(search, 500)

  const loadOverview = useCallback(async () => {
    try {
      isLoading.onTrue()
      const data = await fetchAuditOverview(searchDebounce, page + 1, perPage, orderBy)
      setOverview(data)
    } catch {
      setOverview(null)
    } finally {
      isLoading.onFalse()
    }
  }, [searchDebounce, page, perPage, orderBy])

  const loadAlerts = useCallback(async () => {
    try {
      const data = await fetchAuditAlerts()
      setAlerts(data.alerts)
      setReviewedAlerts(data.reviewed)
    } catch {
      setAlerts([])
      setReviewedAlerts([])
    }
  }, [])

  const handleMarkReviewed = useCallback(
    async (eventId: string, reviewed: boolean) => {
      try {
        setMarkingReviewId(eventId)
        await markAuditAlertReviewed(eventId, reviewed)
        await loadAlerts()
      } finally {
        setMarkingReviewId(null)
      }
    },
    [loadAlerts],
  )

  const handleUpdated = useCallback(() => {
    loadOverview()
    loadAlerts()
  }, [loadOverview, loadAlerts])

  useEffect(() => {
    loadOverview()
  }, [searchDebounce, page, perPage, orderBy])

  // Alertas são carregados uma vez (independem de busca/paginação).
  useEffect(() => {
    loadAlerts()
  }, [])

  // Ao alterar a busca ou a ordenação, volta para a primeira página.
  useEffect(() => {
    setPage(0)
  }, [searchDebounce, orderBy])

  const totals = overview?.totals
  const events = overview?.events || []

  return (
    <>
      <Helmet>
        <title>Portal da Transparência - Dashboard</title>
      </Helmet>

      <Container maxWidth={settings.themeStretch ? false : 'xl'}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
            borderRadius: 2,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            mb: { xs: 2, md: 3 },
            color: 'white',
          }}
        >
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Iconify icon='solar:shield-check-bold' width={22} />
            </Box>
            <Box>
              <Typography variant='h6' fontWeight={700}>
                Portal da Transparência
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.85 }}>
                Auditoria de eventos — máquinas, vendedores e período de operação
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid xs={12} md={4}>
            <KpiCard
              title='Total de eventos'
              value={String(totals?.events || 0)}
              icon='solar:calendar-bold'
              color='info'
            />
          </Grid>
          <Grid xs={6} md={4}>
            <KpiCard
              title='Eventos ativos'
              value={String(totals?.active || 0)}
              icon='solar:play-circle-bold'
              color='success'
            />
          </Grid>
          <Grid xs={6} md={4}>
            <KpiCard
              title='Com cobrança configurada'
              value={String(totals?.withBilling || 0)}
              icon='solar:banknote-bold'
              color='primary'
            />
          </Grid>
        </Grid>

        {(alerts.length > 0 || reviewedAlerts.length > 0) && (
          <Card
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              mb: 3,
              border: '1px solid',
              borderColor: alerts.length > 0 ? 'error.light' : 'divider',
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              spacing={1.25}
              sx={{
                px: 2.5,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: alerts.length > 0 ? 'error.light' : 'divider',
                bgcolor: alerts.length > 0 ? 'error.lighter' : 'grey.50',
              }}
            >
              <Iconify
                icon={alerts.length > 0 ? 'solar:danger-triangle-bold' : 'solar:check-circle-bold'}
                width={18}
                sx={{ color: alerts.length > 0 ? 'error.main' : 'success.main' }}
              />
              <Typography
                variant='subtitle2'
                fontWeight={700}
                color={alerts.length > 0 ? 'error.main' : 'text.primary'}
              >
                Eventos acima do limite contratado
              </Typography>
              {alerts.length > 0 && (
                <Chip size='small' color='error' variant='soft' label={alerts.length} />
              )}
              {reviewedAlerts.length > 0 && (
                <Button
                  size='small'
                  color='inherit'
                  onClick={() => setShowReviewed((v) => !v)}
                  endIcon={
                    <Iconify
                      icon={showReviewed ? 'eva:arrow-up-fill' : 'eva:arrow-down-fill'}
                      width={16}
                    />
                  }
                  sx={{ ml: 'auto' }}
                >
                  {showReviewed ? 'Ocultar' : 'Ver'} analisados ({reviewedAlerts.length})
                </Button>
              )}
            </Stack>
            {alerts.length > 0 && (
              <AlertsTable
                rows={alerts}
                markingReviewId={markingReviewId}
                onMarkReviewed={handleMarkReviewed}
                onSelect={setSelectedEvent}
              />
            )}
            {alerts.length === 0 && reviewedAlerts.length > 0 && !showReviewed && (
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  Nenhum alerta pendente. Todos os casos foram marcados como analisados.
                </Typography>
              </Box>
            )}
            <Collapse in={showReviewed && reviewedAlerts.length > 0}>
              <Box
                sx={{
                  px: 2.5,
                  py: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant='caption' color='text.secondary' fontWeight={600}>
                  Analisados — voltam a aparecer se o nº de máquinas em uso aumentar
                </Typography>
              </Box>
              <AlertsTable
                rows={reviewedAlerts}
                reviewed
                markingReviewId={markingReviewId}
                onMarkReviewed={handleMarkReviewed}
                onSelect={setSelectedEvent}
              />
            </Collapse>
          </Card>
        )}

        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent='space-between'
            spacing={1.5}
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Stack direction='row' alignItems='center' spacing={1.25}>
              <Iconify icon='solar:list-check-bold' width={18} sx={{ color: 'primary.main' }} />
              <Typography variant='subtitle2' fontWeight={700}>
                Panorama de Eventos
              </Typography>
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <ToggleButtonGroup
                size='small'
                exclusive
                value={orderBy}
                onChange={(_e, value) => {
                  if (value) setOrderBy(value)
                }}
              >
                <ToggleButton value='date'>
                  <Iconify icon='solar:calendar-linear' width={16} sx={{ mr: 0.5 }} />
                  Cadastrados recentes
                </ToggleButton>
                <ToggleButton value='activity'>
                  <Iconify icon='solar:pulse-linear' width={16} sx={{ mr: 0.5 }} />
                  Movimentação recente
                </ToggleButton>
              </ToggleButtonGroup>
              <TextField
                size='small'
                placeholder='Buscar evento...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Iconify icon='solar:magnifer-linear' width={18} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: 1, sm: 260 } }}
              />
            </Stack>
          </Stack>

          {isLoading.value ? (
            <Box sx={{ p: 2.5 }}>
              <Skeleton variant='rounded' height={320} />
            </Box>
          ) : (
            <TableContainer>
              <Scrollbar>
                <Table size='small' sx={{ minWidth: 980 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Evento</TableCell>
                      <TableCell>Responsável</TableCell>
                      <TableCell align='center'>Máquinas (uso / contratado)</TableCell>
                      <TableCell align='right'>Vendedores</TableCell>
                      <TableCell align='right'>Pedidos</TableCell>
                      <TableCell align='right'>Faturamento</TableCell>
                      <TableCell align='right'>1ª venda</TableCell>
                      <TableCell align='right'>Última venda</TableCell>
                      <TableCell align='center'>Situação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {events.map((row) => (
                      <TableRow
                        key={row.eventId}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedEvent(row)}
                      >
                        <TableCell>
                          <Typography variant='body2' fontWeight={600}>
                            {row.name}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {row.local || 'Sem local'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='caption'>
                            {row.managers.map((m) => m.name).join(', ') || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography
                            variant='body2'
                            fontWeight={700}
                            color={row.overLimit ? 'error.main' : 'text.primary'}
                          >
                            {row.terminalsUsed} /{' '}
                            {row.contractedTerminals > 0 ? row.contractedTerminals : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align='right'>{row.sellersCount}</TableCell>
                        <TableCell align='right'>{row.totalOrders}</TableCell>
                        <TableCell align='right'>{formatCurrency(row.totalRevenue)}</TableCell>
                        <TableCell align='right' sx={{ color: 'text.secondary' }}>
                          {fmtDateTime(row.firstSale)}
                        </TableCell>
                        <TableCell align='right' sx={{ color: 'text.secondary' }}>
                          {fmtDateTime(row.lastSale)}
                        </TableCell>
                        <TableCell align='center'>
                          <StatusChip row={row} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {!events.length && (
                      <TableRow>
                        <TableCell colSpan={9} align='center' sx={{ py: 6 }}>
                          <Typography variant='body2' color='text.secondary'>
                            Nenhum evento encontrado.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>
          )}

          <TablePagination
            component='div'
            count={overview?.total || 0}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => {
              setPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage='Eventos por página'
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Card>
      </Container>

      <AuditEventDialog
        eventRow={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onUpdated={handleUpdated}
      />
    </>
  )
}

function AlertsTable({
  rows,
  reviewed = false,
  markingReviewId,
  onMarkReviewed,
  onSelect,
}: {
  rows: AuditOverviewRow[]
  reviewed?: boolean
  markingReviewId: string | null
  onMarkReviewed: (eventId: string, reviewed: boolean) => void
  onSelect: (row: AuditOverviewRow) => void
}) {
  return (
    <TableContainer>
      <Scrollbar>
        <Table size='small' sx={{ minWidth: 780 }}>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox' />
              <TableCell>Evento</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell align='center'>Máquinas (uso / contratado)</TableCell>
              <TableCell align='center'>Excedente</TableCell>
              <TableCell align='right'>Faturamento</TableCell>
              <TableCell align='right'>Última venda</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.eventId}
                hover
                sx={{ cursor: 'pointer', opacity: reviewed ? 0.75 : 1 }}
                onClick={() => onSelect(row)}
              >
                <TableCell padding='checkbox' onClick={(e) => e.stopPropagation()}>
                  <Tooltip
                    title={
                      reviewed
                        ? 'Desmarcar — voltar a exibir como pendente'
                        : 'Marcar como analisado'
                    }
                  >
                    <Checkbox
                      size='small'
                      checked={reviewed}
                      disabled={markingReviewId === row.eventId}
                      onChange={() => onMarkReviewed(row.eventId, !reviewed)}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant='body2' fontWeight={600}>
                    {row.name}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {row.local || 'Sem local'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='caption'>
                    {row.managers.map((m) => m.name).join(', ') || '—'}
                  </Typography>
                </TableCell>
                <TableCell align='center'>
                  <Typography
                    variant='body2'
                    fontWeight={700}
                    color={reviewed ? 'text.secondary' : 'error.main'}
                  >
                    {row.terminalsUsed} / {row.contractedTerminals}
                  </Typography>
                </TableCell>
                <TableCell align='center'>
                  <Chip
                    size='small'
                    color={reviewed ? 'default' : 'error'}
                    variant='soft'
                    label={`+${row.exceededBy ?? row.terminalsUsed - row.contractedTerminals}`}
                  />
                </TableCell>
                <TableCell align='right'>{formatCurrency(row.totalRevenue)}</TableCell>
                <TableCell align='right' sx={{ color: 'text.secondary' }}>
                  {fmtDateTime(row.lastSale)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </TableContainer>
  )
}

function StatusChip({ row }: { row: AuditOverviewRow }) {
  if (row.overLimit) {
    return (
      <Tooltip title='Está usando mais máquinas do que o contratado'>
        <Chip size='small' color='error' variant='soft' label='Acima do limite' />
      </Tooltip>
    )
  }
  if (row.undeclared) {
    return (
      <Tooltip title='Há máquinas em uso, mas nada foi declarado/contratado'>
        <Chip size='small' color='warning' variant='soft' label='Não declarado' />
      </Tooltip>
    )
  }
  if (row.terminalsUsed === 0) {
    return <Chip size='small' variant='soft' label='Sem vendas' />
  }
  return (
    <Chip size='small' color='success' variant='soft' label='Regular' />
  )
}

function KpiCard({
  title,
  value,
  icon,
  color,
}: {
  title: string
  value: string
  icon: string
  color: 'success' | 'warning' | 'primary' | 'info' | 'error'
}) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: `${color}.lighter` }}>
      <Stack direction='row' alignItems='center' spacing={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: `${color}.lighter`,
            color: `${color}.main`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify icon={icon} width={20} />
        </Box>
        <Box>
          <Typography variant='caption' color='text.secondary'>
            {title}
          </Typography>
          <Typography variant='h6' fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}
